import React, { useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ArrowUp, ArrowDown, Eye, EyeOff, Pencil, Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function NavSettings({ items, orientation, onClose, onSaved }) {
  const [list, setList] = useState(items.map((i) => ({ id: i.id, label: i.label, hidden: !!i.hidden })));
  const [orient, setOrient] = useState(orientation || "vertical");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next);
  };
  const setItem = (idx, patch) => setList(list.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/preferences/nav", { orientation: orient, items: list });
      toast.success("Navigation settings saved");
      onSaved(data.nav);
      onClose();
    } catch {
      toast.error("Could not save settings");
    }
    setSaving(false);
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.put("/auth/preferences/nav", { orientation: "vertical", items: [] });
      toast.success("Reset to default navigation");
      onSaved(null);
      onClose();
    } catch {
      toast.error("Could not reset");
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-auto" data-testid="nav-settings-dialog">
        <DialogHeader><DialogTitle>Navigation Settings</DialogTitle></DialogHeader>

        <div className="mb-4">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Navigation bar position</div>
          <div className="grid grid-cols-2 gap-2">
            {[["vertical", "Vertical (side bar)"], ["horizontal", "Horizontal (top bar)"]].map(([v, l]) => (
              <button key={v} onClick={() => setOrient(v)} data-testid={`nav-orient-${v}`}
                className={`text-sm px-3 py-2.5 rounded-lg border font-medium transition-colors ${orient === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Menu items — rename, reorder, hide</div>
        <div className="border rounded-lg divide-y">
          {list.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-1.5 px-2.5 py-1.5" data-testid={`nav-item-row-${it.id}`}>
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" data-testid={`nav-move-up-${it.id}`}><ArrowUp size={12} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="text-slate-300 hover:text-slate-600 disabled:opacity-30" data-testid={`nav-move-down-${it.id}`}><ArrowDown size={12} /></button>
              </div>
              {editing?.id === it.id ? (
                <>
                  <Input className="h-8 flex-1 text-sm" value={editing.label} autoFocus data-testid="nav-rename-input"
                    onChange={(e) => setEditing({ id: it.id, label: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && (setItem(idx, { label: editing.label.trim() || it.label }), setEditing(null))} />
                  <button onClick={() => { setItem(idx, { label: editing.label.trim() || it.label }); setEditing(null); }} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid="nav-rename-save"><Check size={15} /></button>
                  <button onClick={() => setEditing(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X size={15} /></button>
                </>
              ) : (
                <>
                  <span className={`flex-1 text-sm ${it.hidden ? "text-slate-300 line-through" : "text-slate-700"}`}>{it.label}</span>
                  <button onClick={() => setEditing({ id: it.id, label: it.label })} className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Rename" data-testid={`nav-rename-${it.id}`}><Pencil size={14} /></button>
                  <button onClick={() => setItem(idx, { hidden: !it.hidden })} className={`p-1 rounded ${it.hidden ? "text-slate-300 hover:text-slate-500 hover:bg-slate-100" : "text-slate-400 hover:text-amber-600 hover:bg-amber-50"}`} title={it.hidden ? "Show" : "Hide"} data-testid={`nav-hide-${it.id}`}>
                    {it.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4 flex justify-between sm:justify-between">
          <Button variant="ghost" size="sm" onClick={reset} disabled={saving} data-testid="nav-reset"><RotateCcw size={14} className="mr-1" />Reset</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="nav-save">{saving ? "Saving…" : "Save Settings"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
